import React, { useEffect, useState } from 'react';
import AddOrderModal from '../modals/AddOrderModal';
import AddExtraIncomeModal from '../modals/AddExtraIncomeModal';
import MarkExtraIncomeAsPaidModal from '../modals/MarkExtraIncomeAsPaidModal';
import AddRefundModal from '../modals/AddRefundModal';
import UpdateOrderStatusModal from '../modals/UpdateOrderStatusModal';
import OrderCostModal from '../modals/OrderCostModal';
import UploadSupportingDocumentModal from '../modals/UploadSupportingDocumentModal';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Order, ExtraIncome } from '../types';
import FullscreenLoader from '../components/FullScreenLoader';
import ExtraIncomeTable from '../components/ExtraIncomeTable';
import RefundsTable from '../components/RefundsTable';
import OrderItemsTable from '../components/OrderItemsTable';
import SupportingDocumentsSection from '../components/SupportingDocumentsSection';
import OrderStatusTimeline from '../components/OrderStatusTimeline';
import { formatCurrency } from '../util/formatCurrency';
import { suggestedFeeRationale } from '../util/suggestedFeeRationale';
import { statusColorMap } from '../util/statusColors';
import { toast } from 'react-toastify';
import ConfirmationModal from '../modals/ConfirmationModal';
import StoreCartModal from '../modals/StoreCartModal';
import CollectionDetailsModal from '../modals/CollectionDetailsModal';
import UpdatePickUpPointModal from '../modals/UpdatePickUpPointModal';
import { useAuth } from '../hooks/useAuth';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import ReferralDiscountCard from '../components/ReferralDiscountCard';
import { InvoiceRemindersPausedBadge } from '../components/invoiceUi';
import ReferralDiscountReasonModal from '../modals/ReferralDiscountReasonModal';
import { formatDiscount, referralDiscountLabel } from '../util/referralDiscount';

const OrderDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);

  const getPaymentMethodText = (method: string) => {
    const paymentMethods: { [key: string]: string } = {
      "eft": "EFT/Cash Deposit/Unayo/eWallet",
      "card": "Online Card Payment(Visa/Mastercard)"
    };
    return paymentMethods[method] || method;
  };

  const formatExportDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Same modal, opened against a copy of this order rather than this order itself.
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isUpdatingInvoiceReminders, setIsUpdatingInvoiceReminders] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtraIncomeModalOpen, setIsExtraIncomeModalOpen] = useState(false);
  const [selectedExtraIncome, setSelectedExtraIncome] = useState<ExtraIncome | null>(null);
  const [isMarkAsPaidModalOpen, setIsMarkAsPaidModalOpen] = useState(false);
  const [selectedExtraIncomeForPayment, setSelectedExtraIncomeForPayment] = useState<ExtraIncome | null>(null);
  const [openExtraIncomeMenuId, setOpenExtraIncomeMenuId] = useState<number | null>(null);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [isExportingCustomerPdf, setIsExportingCustomerPdf] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState<any | null>(null);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [isYocoModalOpen, setIsYocoModalOpen] = useState(false);
  const [isOrderCostModalOpen, setIsOrderCostModalOpen] = useState(false);
  const [showPdfExportConfirmModal, setShowPdfExportConfirmModal] = useState(false);
  const [showPdfNotificationConfirmModal, setShowPdfNotificationConfirmModal] = useState(false);
  const [showFeedbackConfirmModal, setShowFeedbackConfirmModal] = useState(false);
  const [isRequestingFeedback, setIsRequestingFeedback] = useState(false);
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] = useState(false);
  const [isStoreCartModalOpen, setIsStoreCartModalOpen] = useState(false);
  const [isCollectionDetailsModalOpen, setIsCollectionDetailsModalOpen] = useState(false);
  const [isUpdatePickUpPointModalOpen, setIsUpdatePickUpPointModalOpen] = useState(false);
  const { user } = useAuth();
  const referralProgrammeEnabled = useFeatureFlag('referral-programme');
  const [referralDiscountAction, setReferralDiscountAction] = useState<'attach' | 'release' | null>(null);
  const [isSavingReferralDiscount, setIsSavingReferralDiscount] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await fetchWithAuth(`/orders/${id}`);
        const data = await res.json();
        setOrder(data);
      } catch (err) {
        console.error('Failed to fetch order', err);
      }
    };

    setIsLoading(true);
    Promise.all([fetchOrder()])
      .finally(() => setIsLoading(false));
  }, [id]);

  const handlePdfExportConfirm = () => {
    setShowPdfExportConfirmModal(false);
    setShowPdfNotificationConfirmModal(true);
  };

  const handleFinalPdfConfirm = async (sendNotification: boolean) => {
    setShowPdfNotificationConfirmModal(false);
    if (!order) return;

    setIsExportingCustomerPdf(true);
    try {
      const res = await fetchWithAuth(`/orders/${order.id}/export_customer_invoice`, {
        method: 'POST',
        body: JSON.stringify({ send_notification: sendNotification }),
      });
      const data = await res.json();
      if (data.message) {
        // Refresh the order to get updated export tracking fields
        const orderRes = await fetchWithAuth(`/orders/${order.id}`);
        const updatedOrder = await orderRes.json();
        setOrder(updatedOrder);
        toast.success(data.message);
      } else {
        toast.error('Customer PDF export failed.');
      }
    } catch (err) {
      toast.error('An error occurred while exporting customer PDF.');
    } finally {
      setIsExportingCustomerPdf(false);
    }
  };

  const handleRequestFeedbackConfirm = async () => {
    setShowFeedbackConfirmModal(false);
    if (!order) return;

    setIsRequestingFeedback(true);
    try {
      const res = await fetchWithAuth(`/orders/${order.id}/request_invoice_feedback`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.message) {
        toast.success(data.message);
      }
    } catch (err) {
      toast.error('An error occurred while requesting feedback.');
    } finally {
      setIsRequestingFeedback(false);
    }
  };

  const updateIsTest = async (isTest: boolean) => {
    try {
      const res = await fetchWithAuth(`/orders/${id}/update_is_test`, {
        method: 'PATCH',
        body: JSON.stringify({ is_test: isTest }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.errors?.[0] || 'Failed to update the order.');
        return;
      }
      // The endpoint returns a bare order, without the computed totals and
      // associations the page renders, so patch the flag rather than replace it.
      setOrder(prev => (prev ? { ...prev, is_test: data.order.is_test } : prev));
      toast.success(isTest ? 'Marked as a test order. It no longer counts towards any figures.' : 'No longer a test order.');
    } catch {
      toast.error('Failed to update the order.');
    }
  };

  const updateInvoiceReminders = async (paused: boolean) => {
    setIsUpdatingInvoiceReminders(true);
    try {
      const res = await fetchWithAuth(`/orders/${id}/update_invoice_reminders`, {
        method: 'PATCH',
        body: JSON.stringify({ paused }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.errors?.[0] || 'Failed to update invoice reminders.');
        return;
      }
      setOrder(prev => (prev ? {
        ...prev,
        invoice_reminders_paused_at: data.order.invoice_reminders_paused_at,
        invoice_reminders_paused_by: data.order.invoice_reminders_paused_by,
      } : prev));
      toast.success(paused ? 'Invoice reminders paused for this order.' : 'Invoice reminders resumed.');
    } catch {
      toast.error('Failed to update invoice reminders.');
    } finally {
      setIsUpdatingInvoiceReminders(false);
    }
  };

  const refreshOrder = () =>
    fetchWithAuth(`/orders/${id}`)
      .then(res => res.json())
      .then(setOrder)
      .catch(err => console.error('Failed to refresh order', err));

  const changeReferralDiscount = async (reason: string) => {
    if (!order || !referralDiscountAction) return;

    setIsSavingReferralDiscount(true);
    try {
      const res = await fetchWithAuth(`/orders/${order.id}/${referralDiscountAction}_referral_discount`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        toast.error(data?.errors?.[0] || 'Could not update the referral discount.');
        return;
      }
      toast.success(data.message);
      setReferralDiscountAction(null);
      await refreshOrder();
    } catch {
      toast.error('Could not update the referral discount.');
    } finally {
      setIsSavingReferralDiscount(false);
    }
  };

  if (isLoading || !order) {
    return <FullscreenLoader message="Loading Order..." />;
  }

  // Once ready for collection the parcel is on a shelf and the customer holds a code
  // pointing at it, so only an admin may move it.
  const isAdminOrNotReady = order.status !== 'ready_for_collection' || user?.role === 'admin';

  const canEditCollectionDetails =
    order.all_items_in_eswatini &&
    order.status !== 'collected' &&
    isAdminOrNotReady;

  // Deliberately not gated on all_items_in_eswatini: whether an item suits a pick-up point
  // is knowable from its description, long before the goods land.
  const canChangeToDefaultPickUpPoint =
    !order.pickup_point_is_default &&
    order.status !== 'collected' &&
    order.status !== 'cancelled' &&
    isAdminOrNotReady;

  return (
    <div className="px-4 md:px-6 py-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Order Details</h1>
          {!['ready_for_collection', 'collected', 'cancelled'].includes(order.status) && (
            <p className="text-sm text-gray-600 mt-1">
              <strong>Estimated Ready for Collection Date:</strong>{' '}
              {order.estimated_collection_date
                ? new Date(order.estimated_collection_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                : 'N/A'}
            </p>
          )}
          {order.status === 'submitted' && order.pickup_point_location && (
            <div className="text-sm text-gray-600 mt-1 flex items-center gap-2">
              <span><strong>Pick-Up Point:</strong> {order.pickup_point_location}</span>
              {canChangeToDefaultPickUpPoint && (
                <button
                  onClick={() => setIsUpdatePickUpPointModalOpen(true)}
                  className="text-blue-600 hover:text-blue-800 text-xs underline"
                >
                  Change to Default
                </button>
              )}
            </div>
          )}
          {order.status !== 'submitted' && (
            <div className="text-sm text-gray-600 mt-1">
              <strong>Collection Details:</strong>
              <div className="ml-2">
                <div>Flyers: {order.flyers != null ? order.flyers : 'N/A'} | Boxes: {order.boxes != null ? order.boxes : 'N/A'} | Parcels: {order.parcels != null ? order.parcels : 'N/A'}</div>
                {order.pickup_point_location && (
                  <div className="flex items-center gap-2">
                    <span>Pick-Up Point: {order.pickup_point_location}</span>
                    {canChangeToDefaultPickUpPoint && (
                      <button
                        onClick={() => setIsUpdatePickUpPointModalOpen(true)}
                        className="text-blue-600 hover:text-blue-800 text-xs underline"
                      >
                        Change to Default
                      </button>
                    )}
                  </div>
                )}
                {order.shelf_compartment && (
                  <div>Shelf: {order.shelf_compartment.code}</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 w-full md:w-auto">
          <button
            className={`bg-black text-white px-4 py-2 rounded ${
              order.can_edit ? 'bg-black hover:bg-gray-800' : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={() => {
              setIsDuplicating(false);
              setIsModalOpen(true);
            }}
            disabled={!order.can_edit}
          >
            Edit Order
          </button>
          {/* Not gated on can_edit -- a collected or cancelled order is still worth copying. */}
          <button
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
            onClick={() => {
              setIsDuplicating(true);
              setIsModalOpen(true);
            }}
          >
            Duplicate Order
          </button>
          <button
            className={`px-4 py-2 rounded text-white ${
              canEditCollectionDetails ? 'bg-black hover:bg-gray-800' : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={() => setIsCollectionDetailsModalOpen(true)}
            disabled={!canEditCollectionDetails}
          >
            Edit Collection Details
          </button>
          <button
            className={`px-4 py-2 rounded text-white ${
              order.order_invoice_url ? 'bg-black hover:bg-gray-800' : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={() => {
              if (order.order_invoice_url) {
                window.open(order.order_invoice_url, '_blank');
              }
            }}
            disabled={!order.order_invoice_url}
          >
            View Invoice
          </button>
          <button
            className={`px-4 py-2 rounded text-white ${
              order.status === "collected" ? "bg-gray-300 cursor-not-allowed" : "bg-blue-700 hover"
            }`}
            onClick={async () => {
              setIsGeneratingInvoice(true);
              try {
                const res = await fetchWithAuth(`/orders/${order.id}/generate_invoice`, {
                  method: 'POST'
                });
                const data = await res.json();
                if (data.invoice_url) {
                  setOrder({ ...order, order_invoice_url: data.invoice_url, status: data.order_status });
                  window.open(data.invoice_url, '_blank');
                } else {
                  toast.error('Invoice generation failed.');
                }
              } catch (err) {
                toast.error('An error occurred while generating the invoice.');
              } finally {
                setIsGeneratingInvoice(false);
              }
            }}
            disabled={!order || isGeneratingInvoice || order.status === 'collected'}
          >
            Generate Invoice
          </button>
          <button
            className={`px-4 py-2 rounded text-white ${
              order.order_invoice_url && !isExportingCustomerPdf
                ? 'bg-blue-700 hover:bg-blue-800'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={() => {
              setShowPdfExportConfirmModal(true);
            }}
            disabled={!order.order_invoice_url || isExportingCustomerPdf}
            title="Downloads the Google Doc invoice as a PDF and attaches it to the order. You can optionally send a WhatsApp notification to the customer."
          >
            {order.has_customer_invoice_pdf ? 'Re-export Invoice' : 'Export Invoice'}
          </button>
          <button
            className={`h-16 w-16 rounded-full overflow-hidden border border-gray-300 ${
              order.order_invoice_url && order.slack_order_form_link && !isRequestingFeedback
                ? 'hover:ring-2 hover:ring-blue-500 cursor-pointer'
                : 'opacity-40 grayscale cursor-not-allowed'
            }`}
            onClick={() => {
              setShowFeedbackConfirmModal(true);
            }}
            disabled={!order.order_invoice_url || !order.slack_order_form_link || isRequestingFeedback}
            title="Request Isaac's feedback"
            aria-label="Request Isaac's feedback"
          >
            <img src="/isaac-feedback.png" alt="Request Isaac's feedback" className="h-full w-full object-cover" />
          </button>
            <button
              className={`px-4 py-2 rounded text-white ${
                order.status === "collected" ? "bg-gray-300 cursor-not-allowed" : "bg-blue-700 hover"
              }`}
              onClick={() => {
                if (!order.payment_method && order.status !== "cancelled") {
                  toast.error("Please set the payment method before updating the status.");
                  return;
                }
                setIsUpdateStatusModalOpen(true);
              }}
              disabled={order.status === "collected"}
            >
            Update Status
            </button>
        </div>
      </div>

      {/* Invoice Export Tracking */}
      <div className="flex justify-end">
        <div className="bg-gray-50 border rounded-md p-3 text-sm">
          <p className="flex items-center gap-2">
            <span className="flex items-center gap-2">
              <strong>Invoice Last Exported:</strong>{' '}
              {order.invoice_last_exported_at ? (
                <>
                  {formatExportDate(order.invoice_last_exported_at)}{' '}
                  <span className={`px-2 py-1 rounded text-xs ${order.invoice_last_export_notification_sent ? 'bg-green-200' : 'bg-gray-200'}`}>
                    {order.invoice_last_export_notification_sent ? '(notification sent)' : '(no notification)'}
                  </span>
                </>
              ) : (
                'Never'
              )}
              {order.invoice_outdated_at && order.invoice_last_exported_at && (
                <span
                  className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700"
                  title="The order total changed since the customer was last sent this invoice"
                >
                  Out of date: export again
                </span>
              )}
            </span>
            {order.has_customer_invoice_pdf ? (
              <button
                onClick={async () => {
                  try {
                    const response = await fetchWithAuth(`/orders/${order.id}/download_customer_invoice`);
                    if (response.ok) {
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      const safeName = (order.customer?.full_name ?? 'customer').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
                      a.download = `${safeName}_${order.order_reference}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } else {
                      toast.error('Failed to download invoice PDF');
                    }
                  } catch (error) {
                    toast.error('Error downloading invoice PDF');
                  }
                }}
                className="text-blue-600 hover:text-blue-800 inline-flex items-center p-1"
                title="Download exported invoice PDF"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            ) : (
              <span className="text-gray-400 inline-flex items-center p-1" title="No PDF available - export an invoice first">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </span>
            )}
          </p>
          {(order.invoice_reminders_paused_at || (order.invoice_outdated_at && !['cancelled', 'collected'].includes(order.status))) && (
            <p className="mt-2 flex flex-wrap items-center gap-2">
              <strong>Invoice Reminders:</strong>
              {order.invoice_reminders_paused_at ? (
                <>
                  <InvoiceRemindersPausedBadge />
                  {order.invoice_reminders_paused_by && (
                    <span className="text-gray-500">by {order.invoice_reminders_paused_by}</span>
                  )}
                </>
              ) : (
                <span>Every 10 minutes in Slack</span>
              )}
              <button
                type="button"
                onClick={() => updateInvoiceReminders(!order.invoice_reminders_paused_at)}
                disabled={isUpdatingInvoiceReminders}
                aria-label={isUpdatingInvoiceReminders ? 'Saving invoice reminders' : order.invoice_reminders_paused_at ? 'Resume invoice reminders' : 'Pause invoice reminders'}
                className="text-blue-600 hover:text-blue-800 text-xs underline whitespace-nowrap disabled:opacity-50"
              >
                {isUpdatingInvoiceReminders ? 'Saving...' : order.invoice_reminders_paused_at ? 'Resume' : 'Pause'}
              </button>
            </p>
          )}
        </div>
      </div>

      <ReferralDiscountCard
        order={order}
        canAttach={referralProgrammeEnabled}
        onRelease={() => setReferralDiscountAction('release')}
        onAttach={() => setReferralDiscountAction('attach')}
      />

      <div className="bg-white shadow rounded-md p-4 space-y-2 text-sm text-gray-700">
        <p>
          <strong>Order Reference:</strong> {order.order_reference}
          {order.is_test && (
            <span className="ml-2 inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
              TEST
            </span>
          )}
        </p>
        <p className="flex items-center gap-2">
          <strong>Test Order:</strong>
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={order.is_test}
            onChange={e => updateIsTest(e.target.checked)}
          />
          <span className="text-xs text-gray-400">excluded from all reporting figures</span>
        </p>
        <p>
          <strong>Migrated Order:</strong>{' '}
          <span className={order.migrated_from_sheets ? 'px-2 py-1 rounded bg-green-200' : 'px-2 py-1 rounded bg-red-200'}>
            {order.migrated_from_sheets ? 'YES' : 'NO'}
          </span>
        </p>
        <p>
          <strong>Costs Confirmed:</strong>{' '}
          <span className={order.order_items_cost_confirmed ? 'px-2 py-1 rounded bg-green-200' : 'px-2 py-1 rounded bg-red-200'}>
            {order.order_items_cost_confirmed ? 'YES' : 'NO'}
          </span>
        </p>
        <p>
          <strong>Supporting Documents:</strong>{' '}
          <span className={order.supporting_documents_count > 0 ? 'px-2 py-1 rounded bg-green-200' : 'px-2 py-1 rounded bg-red-200'}>
            {order.supporting_documents_count > 0 ? `YES (${order.supporting_documents_count})` : 'NO'}
          </span>
        </p>
        <br/>
        <p>
          <strong>Customer:</strong>{' '}
          {order.customer ? (
            <a
              href={`/customers/${order.customer_id}`}
              className="text-blue-600 underline"
            >
              {order.customer.full_name}
            </a>
          ) : (
            <span className="text-gray-500">Unknown customer</span>
          )}
        </p>
        <p><strong>Date:</strong> {order.date}</p>
        <p>
          <strong>Origin:</strong>{' '}
          <span
            className={`px-2 py-1 rounded ${
              order.origin === 'WHATSAPP' ? 'bg-green-200' : order.origin === 'WEBSITE' ? 'bg-blue-200' : 'bg-gray-200'
            }`}
          >
            {order.origin}
          </span>
        </p>
        <p>
          <strong>Auto-extracted:</strong>{' '}
          <span className={order.auto_extraction ? 'px-2 py-1 rounded bg-green-200' : 'px-2 py-1 rounded bg-red-200'}>
            {order.auto_extraction ? 'YES' : 'NO'}
          </span>
        </p>
        <p><strong>Payment Method:</strong> {order.payment_method}</p>
        <p>
          <strong>Customer Purchasing for themselves?:</strong>{' '}
          <span className={order.is_customer_purchasing ? 'px-2 py-1 rounded bg-green-200' : 'px-2 py-1 rounded bg-red-200'}>
            {order.is_customer_purchasing ? 'YES' : 'NO'}
          </span>
        </p>
        {order.is_customer_purchasing && (
          <p className="text-gray-600 italic">
            <strong>Items paid for by customer (internal record):</strong>{' '}
            {formatCurrency(order.customer_purchased_items_total ?? 0)}
          </p>
        )}
        <br/>
        <p><strong>Categories:</strong> {order.categories && order.categories.length > 0 ? (
          order.categories
            .sort((a, b) => a.category.localeCompare(b.category))
            .map((category) => category.category)
            .join(', ')
        ) : 'None'}</p>
        <br/>
        <strong>Status:</strong> <span className={`px-2 py-1 rounded ${statusColorMap[order.status]?.class || 'bg-gray-100'}`}>
          {statusColorMap[order.status]?.text || 'Unknown'}
        </span>
        <p>
          <strong>Pre-Ready for Collection:</strong>{' '}
          <span className={order.pre_ready_for_collection ? 'px-2 py-1 rounded bg-green-200' : 'px-2 py-1 rounded bg-gray-200'}>
            {order.pre_ready_for_collection ? 'YES' : 'NO'}
          </span>
        </p>
        <p>
          <strong>All Items in Eswatini:</strong>{' '}
          <span className={order.all_items_in_eswatini ? 'px-2 py-1 rounded bg-green-200' : 'px-2 py-1 rounded bg-red-200'}>
            {order.all_items_in_eswatini ? 'YES' : 'NO'}
          </span>
        </p>
        <br />
        <p><strong>Slack Link:</strong> <a href={order.slack_order_form_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{order.slack_order_form_link}</a></p>
        <p><strong>Payment Link:</strong> <a href={order.payment_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{order.payment_link}</a></p>
        <p><strong>Notes:</strong> {order.notes}</p>
        <br />
        {(order.suggested_delivery_fee != null || order.suggested_delivery_fee_metadata?.status === 'needs_quote') && (
          <>
            <p>
              <strong>AI Suggested Delivery Fee:</strong>{' '}
              {order.suggested_delivery_fee != null
                ? formatCurrency(order.suggested_delivery_fee)
                : 'Manual quote suggested'}
            </p>
            <p className="text-xs text-gray-400 italic mb-1">
              {suggestedFeeRationale(order.suggested_delivery_fee_metadata)}This is an AI-suggested delivery fee. The admin should decide what the delivery fee should be.
            </p>
            <br />
          </>
        )}
        <p><strong>Order Items Total Price:</strong> {formatCurrency(order.order_items_total_price)}</p>
        <p><strong>Delivery Fee:</strong> {formatCurrency(order.tini_delivery_fee)}</p>
        {order.referral_discount && (
          <p className="flex items-center gap-2">
            <span>
              <strong>{referralDiscountLabel(order.referral_discount, order.referral_discount.capped)}:</strong>{' '}
              <span className="text-red-500">{formatDiscount(order.referral_discount.amount)}</span>
            </span>
            {order.referral_discount.status === 'reserved' ? (
              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
                Reserved
              </span>
            ) : (
              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Redeemed</span>
            )}
          </p>
        )}
        <p><strong>Collection Fee:</strong> {formatCurrency(order.collection_fee_amount)}</p>
        <p><strong>Payment Charge:</strong> {formatCurrency(order.order_payment_charge)}</p>
        <p><strong>Documentation Fee:</strong> {formatCurrency(order.documentation_fee)}</p>
        <p><strong>VAT:</strong> {formatCurrency(order.vat)}</p>
        <br />
        <p>
          <strong>Credit/Discount:</strong>{' '}
          <span className={order.credit_amount > 0 ? 'text-red-500' : 'text-black'}>
            {formatCurrency(order.credit_amount)}
          </span>
        </p>
      <br />
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <p className="text-base flex items-center gap-2">
          <strong>ORDER TOTAL:</strong> {formatCurrency(order.total_price)}
          <button
            className="inline-block bg-gray-200 text-gray-800 rounded-full w-5 h-5 text-xs text-center"
            title={`Yoco Total: E${order.yoco_amount}`}
            onClick={() => setIsYocoModalOpen(true)}
          >
            Y
          </button>
        </p>
        <div className="flex flex-col items-start md:items-end md:ml-auto">
          <span><strong>ORDER ITEMS TOTAL COST:</strong> {formatCurrency(order.calculate_order_items_total_cost)}</span>
          <span><strong>ORDER ITEMS NET COST:</strong> <span className={`${(order.order_items_profit_loss ?? 0) > 0 ? 'text-green-600' : (order.order_items_profit_loss ?? 0) < 0 ? 'text-red-600' : 'text-black'}`}>{formatCurrency(order.order_items_profit_loss ?? 0)}</span></span>
        </div>
      </div>
      </div>

      <div className="flex flex-col md:flex-row justify-end items-stretch md:items-start gap-4">
        <OrderStatusTimeline statusHistory={order.status_history} currentStatus={order.status} />
        <button
          onClick={() => setIsOrderCostModalOpen(true)}
          className={`px-4 py-2 rounded text-white whitespace-nowrap ${
            order.order_items_cost_confirmed
              ? "bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
              : "bg-blue-700 text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
          }`}
        >
          {order.order_items_cost_confirmed
            ? "Update Order Item Cost"
            : "Confirm Order Item Cost"}
        </button>
      </div>

      {order.formatted_app_order_payload && (
        <div className="bg-white shadow rounded-md p-4 space-y-2 text-sm text-gray-700 mb-6 border-t border-gray-200">
          <h2 className="text-lg font-semibold mb-2">App Order Details</h2>
          <p><strong>Purchase Type:</strong> {order.formatted_app_order_payload.purchase_type}</p>
          <p><strong>Payment Method:</strong> {getPaymentMethodText(order.formatted_app_order_payload.payment_method)}</p>
          {order.formatted_app_order_payload.item_links_and_info && order.formatted_app_order_payload.item_links_and_info.length > 0 && (
            <div>
              <p className="font-semibold mt-2">Item Links and Info:</p>
              <ul className="list-disc pl-5">
                {order.formatted_app_order_payload.item_links_and_info.map((item: any, index: number) => (
                  <li key={index}>
                    {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{item.link}</a>}
                    {item.additional_info && ` - ${item.additional_info}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {order.external_form_payload && (
        <div className="bg-white shadow rounded-md p-4 space-y-2 text-sm text-gray-700 mb-6 border-t border-gray-200">
          <h2 className="text-lg font-semibold mb-2">External Form Submission Details</h2>
          <p><strong>Tini Express Makes Purchase?:</strong> {order.external_form_payload.is_tini_making_purchase}</p>
          <p><strong>Payment Method:</strong> {order.external_form_payload.payment_method || 'Not specified'}</p>
          {order.external_form_payload.item_links && (
            <div>
              <p className="font-semibold mt-2">Item Links:</p>
              <ul className="list-disc pl-5">
                {order.external_form_payload.item_links.split(',').map((link: string, index: number) => (
                  <li key={index}>
                    <a href={link.trim()} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      {link.trim()}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {order.external_form_payload.additional_info && (
            <p><strong>Additional Info:</strong> {order.external_form_payload.additional_info}</p>
          )}
          {order.external_form_payload.referral_code && (
            <p><strong>Referral Code:</strong> {order.external_form_payload.referral_code}</p>
          )}
          {order.external_form_payload.source && (
            <p><strong>How did they hear about us?:</strong> {order.external_form_payload.source}</p>
          )}
        </div>
      )}
      <OrderItemsTable
        orderId={order.id}
        orderItems={order.order_items}
        autoExtraction={order.auto_extraction}
        vatIsCustom={order.vat_is_custom}
        onSaArrival={() => {
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
        }}
        onEswatiniArrival={() => {
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
        }}
        onViewCart={order.store_cart ? () => setIsStoreCartModalOpen(true) : undefined}
        cartStoreName={order.store_cart?.store.name ?? (order.auto_extraction && order.order_items.some(item => item.store.name === 'SHEIN') ? 'SHEIN' : undefined)}
      />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 className="text-lg font-semibold">Extra Incomes</h3>
        <button
          onClick={() => setIsExtraIncomeModalOpen(true)}
          className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
        >
          Add Extra Income
        </button>
      </div>
      {order.extra_incomes.length > 0 ? (
        <ExtraIncomeTable
          extraIncomes={order.extra_incomes}
          openActionMenuId={openExtraIncomeMenuId}
          setOpenActionMenuId={setOpenExtraIncomeMenuId}
          onEdit={(income) => {
            setSelectedExtraIncome(income);
            setIsExtraIncomeModalOpen(true);
          }}
          onMarkAsPaid={(income) => {
            setSelectedExtraIncomeForPayment(income);
            setIsMarkAsPaidModalOpen(true);
          }}
          hideOrderDetails={true}
          onSuccess={() => {
            fetchWithAuth(`/orders/${id}`)
              .then(res => res.json())
              .then(setOrder)
              .catch(err => console.error('Failed to refresh order', err));
          }}
        />
      ) : (
        <p>There are no extra incomes for this order</p>
      )}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 className="text-lg font-semibold">Refunds</h3>
        <button
          onClick={() => setIsRefundModalOpen(true)}
          className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
        >
          Add Refund
        </button>
      </div>
      {order.refunds.length > 0 ? (
        <RefundsTable
          refunds={order.refunds} 
          onEdit={(refund) => {
            setSelectedRefund(refund);
            setIsRefundModalOpen(true);
          }}
          onSuccess={() => {
            fetchWithAuth(`/orders/${id}`)
              .then(res => res.json())
              .then(setOrder)
              .catch(err => console.error('Failed to refresh order', err));
          }}
        />
      ) : (
        <p>There are no refunds for this order</p>
      )}

      {/* Supporting Documents Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 className="text-lg font-semibold">
          Supporting Documents
          {order.supporting_documents_count > 0 && (
            <span className="ml-2 text-sm text-gray-500">
              ({order.supporting_documents_count})
            </span>
          )}
        </h3>
        <button
          onClick={() => setIsUploadDocumentModalOpen(true)}
          className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
        >
          Upload Document
        </button>
      </div>
      <SupportingDocumentsSection
        orderId={order.id}
        documents={order.supporting_documents}
        onDocumentsChange={() => {
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
        }}
      />

      {order && (
        <AddOrderModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setIsDuplicating(false);
          }}
          onSuccess={(savedOrder) => {
            const wasDuplicating = isDuplicating;
            setIsModalOpen(false);
            setIsDuplicating(false);
            // A duplicate is a different order, so show it rather than leaving the admin on the
            // one they copied -- otherwise the page looks unchanged and the new order is easy
            // to lose track of.
            if (wasDuplicating && savedOrder?.id) {
              navigate(`/orders/${savedOrder.id}`);
              return;
            }
            fetchWithAuth(`/orders/${id}`)
              .then(res => res.json())
              .then((updatedOrder) => {
                setOrder(updatedOrder); // Force update to reflect new order_items
              })
              .catch(err => console.error('Failed to refresh order', err));
          }}
          order={isDuplicating ? undefined : order}
          duplicateFromOrder={isDuplicating ? order : undefined}
          onReferralDiscountReleased={refreshOrder}
        />
      )}
      <AddExtraIncomeModal
        isOpen={isExtraIncomeModalOpen}
        onClose={() => {
          setIsExtraIncomeModalOpen(false);
          setSelectedExtraIncome(null);
        }}
        onSuccess={() => {
          setIsExtraIncomeModalOpen(false);
          setSelectedExtraIncome(null);
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder);
        }}
        extraIncome={selectedExtraIncome}
        disableOrderSelect={true}
        selectedOrder={{ value: order.id, label: order.identifier }}
      />
      <MarkExtraIncomeAsPaidModal
        isOpen={isMarkAsPaidModalOpen}
        onClose={() => {
          setIsMarkAsPaidModalOpen(false);
          setSelectedExtraIncomeForPayment(null);
        }}
        onSuccess={() => {
          setIsMarkAsPaidModalOpen(false);
          setSelectedExtraIncomeForPayment(null);
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder);
        }}
        extraIncome={selectedExtraIncomeForPayment}
      />
      <AddRefundModal
        isOpen={isRefundModalOpen}
        onClose={() => {
          setIsRefundModalOpen(false);
          setSelectedRefund(null);
        }}
        onSuccess={() => {
          setIsRefundModalOpen(false);
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
        }}
        defaultValue={selectedRefund}
        orders={[order]}
        disableOrderSelect={true}
      />
      <UpdateOrderStatusModal
        isOpen={isUpdateStatusModalOpen}
        onClose={() => setIsUpdateStatusModalOpen(false)}
        onSuccess={() => {
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
        }}
        orderId={order.id}
        hasUnpaidExtraIncome={order.has_unpaid_extra_income}
        referralDiscount={order.referral_discount}
        customerName={order.customer?.full_name}
      />
      <ReferralDiscountReasonModal
        isOpen={referralDiscountAction !== null}
        title={referralDiscountAction === 'attach' ? 'Attach referral discount' : 'Release referral discount'}
        description={
          referralDiscountAction === 'attach'
            ? `Reserves one of ${order.customer?.full_name ?? 'the customer'}'s referral discounts for this order, takes it off the delivery fee and regenerates the payment link.`
            : `Takes ${order.referral_discount ? formatDiscount(order.referral_discount.amount) : 'the discount'} off this order and gives the discount back to ${order.customer?.full_name ?? 'the customer'} for a later order. The payment link is regenerated.`
        }
        confirmLabel={referralDiscountAction === 'attach' ? 'Attach discount' : 'Release discount'}
        savingLabel={referralDiscountAction === 'attach' ? 'Attaching...' : 'Releasing...'}
        placeholder={referralDiscountAction === 'attach' ? 'e.g. Asked for it on WhatsApp' : 'e.g. Order will not go ahead'}
        isSaving={isSavingReferralDiscount}
        onConfirm={changeReferralDiscount}
        onCancel={() => setReferralDiscountAction(null)}
      />
      {isGeneratingInvoice && <FullscreenLoader message="Invoice Generating..." />}
      {isExportingCustomerPdf && <FullscreenLoader message="Exporting Customer PDF..." />}
      <ConfirmationModal 
        isOpen={isYocoModalOpen}
        onConfirm={() => setIsYocoModalOpen(false)}
        onCancel={() => setIsYocoModalOpen(false)}
        message={`Yoco Total: E${order.yoco_amount}`}
        showOnlyOk={true}
        />
      <OrderCostModal
        key={order.id} 
        isOpen={isOrderCostModalOpen}
        onClose={() => setIsOrderCostModalOpen(false)}
        orderId={order.id}
        orderItems={order.order_items}
        onConfirm={() => {
          setIsOrderCostModalOpen(false);
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
          toast.success("Order Item Costs Updated");
        }}
      />

      <UploadSupportingDocumentModal
        isOpen={isUploadDocumentModalOpen}
        onClose={() => setIsUploadDocumentModalOpen(false)}
        onSuccess={() => {
          setIsUploadDocumentModalOpen(false);
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
        }}
        orderId={order.id}
      />

      {showPdfExportConfirmModal && (
        <ConfirmationModal
          isOpen={showPdfExportConfirmModal}
          message={`Are you sure you want to ${order.has_customer_invoice_pdf ? 're-export' : 'export'} the customer invoice PDF?`}
          onConfirm={handlePdfExportConfirm}
          onCancel={() => setShowPdfExportConfirmModal(false)}
        />
      )}

      {showPdfNotificationConfirmModal && (
        <ConfirmationModal
          isOpen={showPdfNotificationConfirmModal}
          message="Would you like to send a notification?"
          onConfirm={() => handleFinalPdfConfirm(true)}
          onCancel={() => handleFinalPdfConfirm(false)}
        />
      )}

      {showFeedbackConfirmModal && (
        <ConfirmationModal
          isOpen={showFeedbackConfirmModal}
          message="Ask Isaac for feedback on this invoice in the order's Slack thread?"
          onConfirm={handleRequestFeedbackConfirm}
          onCancel={() => setShowFeedbackConfirmModal(false)}
        />
      )}
      <CollectionDetailsModal
        isOpen={isCollectionDetailsModalOpen}
        onClose={() => setIsCollectionDetailsModalOpen(false)}
        onSuccess={() => {
          fetchWithAuth(`/orders/${id}`)
            .then(res => res.json())
            .then(setOrder)
            .catch(err => console.error('Failed to refresh order', err));
        }}
        orderId={order.id}
        currentFlyers={order.flyers}
        currentBoxes={order.boxes}
        currentParcels={order.parcels}
        currentPickUpPointId={order.pick_up_point_id}
        currentPickUpPointLocation={order.pickup_point_location}
        currentShelfCompartmentId={order.shelf_compartment?.id ?? null}
      />
      {order.store_cart && (
        <StoreCartModal
          isOpen={isStoreCartModalOpen}
          onClose={() => setIsStoreCartModalOpen(false)}
          storeCart={order.store_cart}
          orderId={order.id}
          orderStatus={order.status}
          onConfirmed={() => fetchWithAuth(`/orders/${id}`).then((r) => r.json()).then(setOrder)}
        />
      )}
      {order.pickup_point_location && !order.pickup_point_is_default && (
        <UpdatePickUpPointModal
          isOpen={isUpdatePickUpPointModalOpen}
          onClose={() => setIsUpdatePickUpPointModalOpen(false)}
          onSuccess={() => {
            fetchWithAuth(`/orders/${id}`)
              .then(res => res.json())
              .then(setOrder)
              .catch(err => console.error('Failed to refresh order', err));
          }}
          orderId={order.id}
          currentPickUpPointLocation={order.pickup_point_location}
          orderStatus={order.status}
        />
      )}
    </div>
  );
};

export default OrderDetails;
